<template>
  <div class="container-fluid mt-3">
    <b-card class="mb-3" header="AI Report Assistant" header-bg-variant="primary" header-text-variant="white">
      <b-row>
        <b-col md="9">
          <b-form-textarea
            v-model="prompt"
            rows="3"
            max-rows="5"
            placeholder="Type simple report request, for example: caste wise application list of July 2026"
          ></b-form-textarea>
        </b-col>
        <b-col md="3" class="d-flex align-items-start justify-content-end">
          <b-form-checkbox v-if="isAdvancedRole" v-model="allowAdvancedSql" class="mr-2 mt-1">
            Advanced SQL
          </b-form-checkbox>
          <b-button variant="success" :disabled="isLoading || !prompt.trim()" @click="generateUrlAndOpen">
            {{ isLoading ? "Generating..." : "Generate URL" }}
          </b-button>
        </b-col>
      </b-row>
      <b-alert show variant="light" class="mt-2 mb-0 border">
        <div><b>Simple guide:</b></div>
        <div><b>Summary report:</b> district wise applications this year</div>
        <div><b>Detailed report:</b> all district application details</div>
        <div><b>Caste report:</b> caste wise application list of July 2026</div>
        <div><b>LOI report:</b> loi details of 2026</div>
        <div><b>Claim report:</b> district wise claim details this month</div>
      </b-alert>
      <b-alert show variant="info" class="mt-2 mb-0" v-if="lastUrl">
        Generated URL: <a :href="lastUrl" @click.prevent="openGeneratedUrl">{{ lastUrl }}</a>
      </b-alert>
    </b-card>

    <b-card v-if="mode === 'advancedSql'" header="AI Generated Report" header-bg-variant="dark" header-text-variant="white" class="mt-3 shadow-sm">
      <div class="d-flex align-items-center mb-2">
        <span class="text-muted small mr-auto">{{ advancedRows.length }} record(s)</span>
        <b-button variant="danger" size="sm" class="ml-2" @click="convertToPdf" :disabled="advancedRows.length === 0">PDF</b-button>
        <json-excel :data="advancedRows" :header="excelHeader" style="display:inline-block;" class="ml-2">
          <button type="button" class="btn btn-sm btn-info">Excel</button>
        </json-excel>
      </div>
      <b-table
        show-empty
        head-variant="dark"
        bordered
        striped
        hover
        small
        responsive
        sticky-header="420px"
        empty-text="No records available."
        :items="advancedRows"
      ></b-table>
    </b-card>

    <report-viewer ref="viewer" v-if="mode !== 'advancedSql'" mainCategory="0"></report-viewer>
  </div>
</template>

<script>
import apis from "@/apis";
import ReportViewer from "@/components/ReportViewer/ReportViewer";
import JsonExcel from "vue-json-excel";
import * as jsPDF from "jspdf";
import "jspdf-autotable";
import logger from "@/utils/logger";

export default {
  name: "ChatbotReport",
  components: { ReportViewer, JsonExcel },
  data() {
    return {
      prompt: "",
      allowAdvancedSql: false,
      isLoading: false,
      lastUrl: "",
      mode: "template",
      advancedRows: [],
      reportName: "AI Report",
    };
  },
  computed: {
    isAdvancedRole() {
      const user = this.$store.getters.loggedUser || {};
      return parseInt(user.RoleID, 10) === 10;
    },
    excelHeader() {
      return [
        "MAHARASHTRA RAJYA ITAR MAGASVARGIYA VITTA ANI VIKAS MAHAMANDAL LTD.",
        this.reportName,
        " "
      ];
    }
  },
  mounted() {
    this.handleRouteQuery();
  },
  watch: {
    "$route.query": {
      deep: true,
      handler() {
        this.handleRouteQuery();
      }
    }
  },
  methods: {
    async generateUrlAndOpen() {
      this.isLoading = true;
      try {
        const request = {
          Prompt: this.prompt,
          BoardID: 1,
          AllowAdvancedSql: this.allowAdvancedSql
        };
        const response = await apis.GenerateChatbotReportUrl(request);
        if (response.status === 200 && response.body && response.body.ReportUrl) {
          this.lastUrl = response.body.ReportUrl;
          this.openGeneratedUrl();
        }
      } catch (err) {
        alert("Unable to generate report URL.");
      } finally {
        this.isLoading = false;
      }
    },
    openGeneratedUrl() {
      if (!this.lastUrl) {
        return;
      }
      this.$router.push(this.lastUrl);
    },
    async handleRouteQuery() {
      const q = this.$route.query || {};
      this.mode = q.mode || "template";
      this.reportName = q.name || "AI Report";

      if (this.mode === "advancedSql" && q.sql) {
        await this.loadAdvancedReport(q.sql);
        return;
      }

      if (q.q) {
        try {
          const decoded = JSON.parse(this.base64UrlDecode(q.q));
          this.$store.dispatch("setReportQuery", decoded);
          this.$store.dispatch("setReportName", this.reportName);
          this.$nextTick(() => {
            if (this.$refs.viewer && this.$refs.viewer.newTab) {
              this.$refs.viewer.newTab();
            }
          });
        } catch (err) {
          logger.error(err);
        }
      }
    },
    async loadAdvancedReport(encodedSql) {
      try {
        const sql = this.base64UrlDecode(encodedSql);
        const response = await apis.ExecuteAdvancedSqlReport({ SqlQuery: sql });
        this.advancedRows = response.status === 200 ? response.body : [];
      } catch (err) {
        this.advancedRows = [];
        alert("Unable to load advanced SQL report.");
      }
    },
    base64UrlDecode(input) {
      let base64 = (input || "").replace(/-/g, "+").replace(/_/g, "/");
      while (base64.length % 4 !== 0) {
        base64 += "=";
      }
      return decodeURIComponent(escape(window.atob(base64)));
    },
    convertToPdf() {
      const pdf = new jsPDF("l", "pt");
      const board = "MAHARASHTRA RAJYA ITAR MAGASVARGIYA VITTA ANI VIKAS MAHAMANDAL LTD.";

      pdf.setFontSize(14);
      pdf.text(board.toUpperCase(), pdf.internal.pageSize.width / 2, 30, { align: "center" });
      pdf.setFontSize(12);
      pdf.text(this.reportName, pdf.internal.pageSize.width / 2, 50, { align: "center" });

      if (this.advancedRows.length === 0) {
        pdf.save("Report.pdf");
        return;
      }

      const firstRow = this.advancedRows[0];
      const columns = Object.keys(firstRow).map((k) => ({ header: k, dataKey: k }));

      pdf.autoTable({
        columns,
        startY: 70,
        body: this.advancedRows,
      });

      pdf.save("Report.pdf");
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/AdminReports/ChatbotReport.vue